// Draft library and research history kept whole on the phone. The server sends a list of (id, fingerprint);
// only new or changed items are downloaded, so searching and opening past items needs no signal.
import { api } from "@/lib/api";
import { copyStale as stale, readCache, remoteChange, writeCache } from "@/lib/cache";


async function sync(key, name, force) {
  const st = (await readCache(key, true))?.data || null;
  if (!force && !stale(st)) return st;
  try {
    const { data } = await api.get(`/local/${name}-index`, { fresh: true, timeout: 60000 });
    const have = new Map((st?.rows || []).map((r) => [r.id, r]));
    const need = data.index.filter(([id, fp]) => have.get(id)?._fp !== fp).map(([id]) => id);
    for (let i = 0; i < need.length; i += 200) {
      const { data: got } = await api.post(`/local/${name}-fetch`, { ids: need.slice(i, i + 200) }, { timeout: 60000 });
      got.items.forEach((r) => have.set(r.id, r));
    }
    const next = { rows: data.index.map(([id]) => have.get(id)).filter(Boolean), localAt: Date.now(), seen: remoteChange() };
    writeCache(key, next);
    return next;
  } catch (e) {
    if (st) return st;
    throw e;
  }
}

export const cachedDrafts = async () => (await readCache("local:drafts", true))?.data || null;
export const syncDrafts = (force = false) => sync("local:drafts", "drafts", force);
export const cachedResearch = async () => (await readCache("local:research", true))?.data || null;
export const syncResearch = (force = false) => sync("local:research", "research", force);

const has = (v, s) => String(v || "").toLowerCase().includes(s);
const listedOn = (d) => d.cross_listed && Object.keys(d.cross_listed).length > 0;

// Same rules as GET /drafts, run on the phone.
export function filterDrafts(rows, { q, category, ids, sku, listed, cleared, ready }) {
  const s = (q || "").trim().toLowerCase().slice(0, 100);
  const idSet = ids ? new Set(ids.split(",").filter(Boolean)) : null;
  const cat = category ? new RegExp(`^${category.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(\\s*>|$)`, "i") : null;
  const out = (rows || []).filter((d) => {
    if (idSet) { if (!idSet.has(d.id)) return false; } else if (!!d.cleared_at !== !!cleared) return false;
    if (ready && (!d.ready_at || listedOn(d) || d.ebay?.listing_id)) return false;
    if (listed === "none" && listedOn(d)) return false;
    if (listed === "any" && !listedOn(d)) return false;
    if (listed && listed !== "none" && listed !== "any" && !d.cross_listed?.[listed]) return false;
    if (s && !has(d.title, s) && !has(d.category, s) && !has(d.sku, s)) return false;
    if (cat && !cat.test(d.category || "")) return false;
    if (sku && (d.sku || "").trim().toLowerCase() !== sku.trim().toLowerCase()) return false;
    return true;
  });
  if (ready) out.sort((a, b) => String(a.ready_at).localeCompare(String(b.ready_at)));
  return out;
}

// Same fields as GET /research?q=: title, category, brand, barcode.
export function filterResearch(rows, q) {
  const s = (q || "").trim().toLowerCase();
  const out = s ? (rows || []).filter((r) => [r.title, r.category, r.key_attrs?.brand, r.key_attrs?.Brand, r.barcode].some((v) => has(v, s))) : rows || [];
  return s ? out.slice(0, 200) : out.slice(0, 50);
}
